import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DotsSixVerticalIcon } from "@phosphor-icons/react/dist/csr/DotsSixVertical";
import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";
import { Button } from "@volca/ui/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { Stack } from "@volca/ui/components/ui/stack";
import { cn } from "@volca/ui/lib/utils";
import type { ReviewRule } from "@/profitability/settings/types";
import { RuleRow } from "./rule-row";

export const SortableRule = ({
  rule,
  reportId,
  runId,
  editable,
  busy,
  canReorder,
  index,
  count,
  onMove,
  onToggle,
}: {
  rule: ReviewRule;
  reportId: string;
  runId: string | null;
  editable: boolean;
  busy: boolean;
  canReorder: boolean;
  index: number;
  count: number;
  onMove: (index: number) => Promise<void>;
  onToggle: (enabled: boolean) => void;
}) => {
  const [optionsOpen, setOptionsOpen] = useState(false);
  const reducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: rule.id, disabled: !canReorder });
  const move = async (position: number) => {
    await onMove(position);
    setOptionsOpen(false);
  };
  return (
    <Stack
      render={<li />}
      ref={setNodeRef}
      className={cn("relative bg-surface-raised", {
        "z-10 rounded-lg shadow-raised": isDragging,
      })}
      style={{
        transform: CSS.Transform.toString(transform && { ...transform, x: 0 }),
        transition:
          transition && !reducedMotion
            ? "transform 160ms cubic-bezier(0.2, 0.8, 0.2, 1)"
            : undefined,
      }}
    >
      <RuleRow
        rule={rule}
        reportId={reportId}
        runId={runId}
        editable={editable}
        busy={busy}
        onToggle={onToggle}
        handle={
          editable &&
          count > 1 && (
            <Popover
              open={optionsOpen && !isDragging}
              onOpenChange={(next) => {
                if (!busy) setOptionsOpen(next);
              }}
            >
              <PopoverTrigger
                render={
                  <Button
                    ref={setActivatorNodeRef}
                    {...attributes}
                    {...listeners}
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-11 shrink-0 cursor-grab touch-none text-subtle active:cursor-grabbing"
                    disabled={!canReorder}
                    aria-label={`Reorder ${rule.label}`}
                    title="Drag to reorder, or click for move options"
                  />
                }
              >
                <DotsSixVerticalIcon aria-hidden />
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-56 motion-reduce:animate-none pointer-coarse:[&_[data-slot=button]]:min-h-11"
              >
                <PopoverTitle>{rule.label}</PopoverTitle>
                <Button
                  variant="ghost"
                  className="justify-start"
                  disabled={index === 0 || busy}
                  onClick={() => move(index - 1)}
                >
                  Move up
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start"
                  disabled={index === count - 1 || busy}
                  onClick={() => move(index + 1)}
                >
                  Move down
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start"
                  disabled={index === 0 || busy}
                  onClick={() => move(0)}
                >
                  Move to top
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start"
                  disabled={index === count - 1 || busy}
                  onClick={() => move(count - 1)}
                >
                  Move to bottom
                </Button>
              </PopoverContent>
            </Popover>
          )
        }
      />
    </Stack>
  );
};
